import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Room, RoomEvent, Track, VideoQuality } from 'livekit-client';
import axios from 'axios';
import { Phone, PhoneOff, Loader2, ShoppingBag, ChevronLeft, ChevronRight } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: false,
  headers: { 'Content-Type': 'application/json' },
});

const EmbedLiveAvatarProductsPage = ({ agentId }) => {
  const [agent, setAgent] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [captions, setCaptions] = useState([]);
  const [room, setRoom] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [avatarVideoTrack, setAvatarVideoTrack] = useState(null);
  const [viewMode, setViewMode] = useState('full');
  const [dynamicVariableNames, setDynamicVariableNames] = useState([]);
  const [dynamicVariableValues, setDynamicVariableValues] = useState({});

  const [productImages, setProductImages] = useState([]);
  const [activeProductId, setActiveProductId] = useState(null);
  const [matchedProductIds, setMatchedProductIds] = useState(new Set());

  const audioRef = useRef(null);
  const videoRef = useRef(null);
  const fpsIntervalRef = useRef(null);
  const videoAttachedRef = useRef(false);
  const avatarReadyNotifiedRef = useRef(false);
  const allCaptionsRef = useRef([]);
  const productDisplayQueueRef = useRef([]);
  const productDisplayIndexRef = useRef(0);
  const lastProductSwitchRef = useRef(0);

  const PRODUCT_DISPLAY_DURATION_MS = 2500;

  // Detect view mode from query string and extract dynamic variable names
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const mode = params.get('view');
    if (mode === 'dynamic') {
      setViewMode('dynamic');
      const systemParams = ['view', 'token', 'embed_token', 'key'];
      const variableNames = [];
      const initialValues = {};
      params.forEach((value, key) => {
        if (!systemParams.includes(key)) {
          variableNames.push(key);
          if (value && !value.startsWith('{{') && !value.endsWith('}}')) {
            initialValues[key] = value;
          }
        }
      });
      setDynamicVariableNames(variableNames);
      setDynamicVariableValues(initialValues);
    }
  }, []);

  useEffect(() => {
    document.documentElement.style.background = 'transparent';
    document.body.style.background = 'transparent';
    document.getElementById('root').style.background = 'transparent';
  }, []);

  // Load agent + product images
  useEffect(() => {
    const loadData = async () => {
      try {
        const [agentRes, imagesRes] = await Promise.all([
          api.get(`/agents/${agentId}`),
          api.get(`/agents/${agentId}/product-images/public`),
        ]);

        if (typeof agentRes.data === 'string' && agentRes.data.trim().startsWith('<!DOCTYPE')) {
          setError('Received unexpected response format.');
          setLoading(false);
          return;
        }

        setAgent(agentRes.data);
        setProductImages(imagesRes.data || []);

        const la = agentRes.data.liveavatar_enabled;
        const enabled = la === true || la === 'true' || la === 1 || la === '1';
        const hasAvatar = agentRes.data.liveavatar_avatar_id && agentRes.data.liveavatar_avatar_id.trim() !== '';

        if (!enabled) setError('LiveAvatar is not enabled for this agent.');
        else if (!hasAvatar) setError('Avatar ID is missing for this agent.');
      } catch (err) {
        setError(`Agent not found: ${err.response?.data?.detail || err.message}`);
      } finally {
        setLoading(false);
      }
    };

    if (agentId) loadData();
    else setLoading(false);
  }, [agentId]);

  // Keyword matching for product images based on conversation text
  // Uses a queue + minimum display duration so when agent lists multiple products,
  // each stays on screen for ~2.5s instead of rapid switching to the last one
  const matchProducts = useCallback(
    (text) => {
      if (!text || !productImages.length) return;
      const lower = text.toLowerCase();

      let bestMatch = null;
      let bestScore = 0;

      productImages.forEach((img) => {
        let score = 0;

        // Check title
        if (lower.includes(img.title.toLowerCase())) {
          score += 10;
        }

        // Check title words (multi-word matching)
        const titleWords = img.title.toLowerCase().split(/\s+/).filter(w => w.length > 2);
        const matchedTitleWords = titleWords.filter(w => lower.includes(w));
        score += matchedTitleWords.length * 3;

        // Check keywords
        let keywords = [];
        if (img.keywords) {
          try { keywords = JSON.parse(img.keywords); } catch { keywords = []; }
        }
        keywords.forEach((kw) => {
          if (lower.includes(kw.toLowerCase())) score += 5;
        });

        // Check description words
        const descWords = img.description.toLowerCase().split(/\s+/).filter(w => w.length > 3);
        const matchedDescWords = descWords.filter(w => lower.includes(w));
        score += matchedDescWords.length;

        if (score > bestScore) {
          bestScore = score;
          bestMatch = img;
        }
      });

      if (bestMatch && bestScore >= 3) {
        const q = productDisplayQueueRef.current;
        if (q.length === 0 || q[q.length - 1] !== bestMatch.id) {
          q.push(bestMatch.id);
        }
        setMatchedProductIds((prev) => new Set(prev).add(bestMatch.id));
      }
    },
    [productImages]
  );

  // Process product display queue: show each product for minimum duration when multiple are listed
  useEffect(() => {
    if (productDisplayQueueRef.current.length === 0) return;
    const tick = () => {
      const q = productDisplayQueueRef.current;
      if (q.length === 0) return;
      const now = Date.now();
      const elapsed = now - lastProductSwitchRef.current;
      const idx = productDisplayIndexRef.current;
      if (idx < q.length) {
        if (elapsed >= PRODUCT_DISPLAY_DURATION_MS || lastProductSwitchRef.current === 0) {
          const nextId = q[idx];
          setActiveProductId(nextId);
          lastProductSwitchRef.current = now;
          productDisplayIndexRef.current = idx + 1;
        }
      }
    };
    tick();
    const id = setInterval(tick, 500);
    return () => clearInterval(id);
  }, [captions]);

  const addCaption = useCallback((speaker, text) => {
    if (!text || !text.trim()) return;
    setCaptions((prev) => {
      const last = prev[prev.length - 1];
      if (last && last.speaker === speaker && last.text === text) return prev;
      const newCaption = { speaker, text, timestamp: new Date().toLocaleTimeString() };
      const updated = [...prev, newCaption];
      allCaptionsRef.current = updated;
      return updated;
    });
  }, []);

  // Append streaming agent segment to current agent caption (for real-time transcript + product switching)
  const appendAgentSegment = useCallback((segment) => {
    if (!segment || !segment.trim()) return;
    setCaptions((prev) => {
      const last = prev[prev.length - 1];
      const updated = [...prev];
      if (last && last.speaker === 'agent') {
        updated[updated.length - 1] = { ...last, text: last.text + segment };
      } else {
        updated.push({ speaker: 'agent', text: segment, timestamp: new Date().toLocaleTimeString() });
      }
      allCaptionsRef.current = updated;
      return updated;
    });
  }, []);

  // Run keyword matching whenever captions change
  useEffect(() => {
    if (captions.length > 0) {
      const last = captions[captions.length - 1];
      if (last.speaker === 'user') {
        productDisplayQueueRef.current = [];
        productDisplayIndexRef.current = 0;
        lastProductSwitchRef.current = 0;
      } else if (last.speaker !== 'system') {
        matchProducts(last.text);
      }
    }
  }, [captions, matchProducts]);

  const updateVariableValue = (name, value) => {
    if (!name) return;
    setDynamicVariableValues(prev => ({ ...prev, [name]: value }));
  };

  const getVariableLabel = (varName) => {
    if (!varName) return '';
    return varName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Start LiveAvatar session
  const startSession = async () => {
    if (!agent) return;
    try {
      setIsConnecting(true);
      setError(null);

      const variablesToSend = {};
      dynamicVariableNames.forEach(name => {
        const value = dynamicVariableValues[name];
        if (value && value.trim()) {
          variablesToSend[name] = value.trim();
        }
      });

      const roomName = `test-agent-${agent.id}-${Date.now()}`;
      const sessionRes = await api.post('/sessions/test', {
        room_name: roomName,
        agent_id: agent.id,
        channel: 'test',
        ...(Object.keys(variablesToSend).length > 0 && { variables: variablesToSend }),
      });
      setSessionId(sessionRes.data.session_id);

      const tokenRes = await api.post('/token', { room: roomName, identity: 'liveavatar-user' });
      const { url, token } = tokenRes.data;

      const newRoom = new Room({
        adaptiveStream: true,
        dynacast: true,
        disconnectOnPageLeave: false,
        videoCaptureDefaults: { resolution: { width: 640, height: 360 } },
        defaultAudioCaptureOptions: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      const attachVideoTrack = (track) => {
        if (!track || !videoRef.current) return false;
        if (videoAttachedRef.current) return true;
        try {
          track.attach(videoRef.current);
          if (!videoRef.current.srcObject && track.mediaStreamTrack) {
            try {
              videoRef.current.srcObject = new MediaStream([track.mediaStreamTrack]);
            } catch {}
          }
          videoAttachedRef.current = true;
          setTimeout(() => {
            if (videoRef.current?.srcObject) videoRef.current.play().catch(() => {});
          }, 300);
          return true;
        } catch { return false; }
      };

      const isAvatarParticipant = (p) =>
        p.identity === 'liveavatar-avatar-agent' ||
        p.name === 'liveavatar-avatar-agent' ||
        (p.identity && p.identity.includes('liveavatar')) ||
        (p.name && p.name.includes('liveavatar'));

      const subscribeToAvatar = (participant) => {
        if (!isAvatarParticipant(participant)) return;
        participant.videoTrackPublications.forEach((pub) => {
          if (pub.track) {
            try { pub.setVideoQuality(VideoQuality.LOW); pub.setVideoFPS(30); } catch {}
            setAvatarVideoTrack(pub.track);
            attachVideoTrack(pub.track);
          }
        });
      };

      newRoom.on(RoomEvent.Connected, () => {
        setIsConnected(true);
        setIsConnecting(false);
        addCaption('system', 'Connected — start speaking!');
        newRoom.remoteParticipants.forEach(subscribeToAvatar);
      });

      newRoom.on(RoomEvent.Disconnected, () => {
        if (fpsIntervalRef.current) { clearInterval(fpsIntervalRef.current); fpsIntervalRef.current = null; }
        videoAttachedRef.current = false;
        avatarReadyNotifiedRef.current = false;
        setIsConnected(false);
        setAvatarVideoTrack(null);
        addCaption('system', 'Disconnected');
      });

      newRoom.on(RoomEvent.ParticipantConnected, subscribeToAvatar);

      newRoom.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
        if (track.kind === 'video' && isAvatarParticipant(participant)) {
          try { publication.setVideoQuality(VideoQuality.LOW); publication.setVideoFPS(30); } catch {}
          setAvatarVideoTrack(track);
          if (!attachVideoTrack(track)) {
            setTimeout(() => { if (videoRef.current && track) attachVideoTrack(track); }, 500);
          }
        } else if (track.kind === 'audio' && audioRef.current) {
          track.attach(audioRef.current);
        }
      });

      newRoom.on(RoomEvent.TrackUnsubscribed, (track, pub, participant) => {
        if (track.kind === 'video' && isAvatarParticipant(participant)) {
          videoAttachedRef.current = false;
          avatarReadyNotifiedRef.current = false;
          setAvatarVideoTrack(null);
          if (videoRef.current) track.detach(videoRef.current);
        }
      });

      newRoom.on(RoomEvent.DataReceived, (payload) => {
        try {
          const data = JSON.parse(new TextDecoder().decode(payload));
          if (data.type === 'agent_transcript_segment') {
            appendAgentSegment(data.text);
          } else if (data.type === 'transcription' || data.type === 'conversation_item') {
            addCaption(data.speaker || 'agent', data.text);
          }
          if (data.type === 'show_product' && data.product_id) {
            setActiveProductId(data.product_id);
            setMatchedProductIds((prev) => new Set(prev).add(data.product_id));
          }
        } catch {}
      });

      await newRoom.connect(url, token);
      setRoom(newRoom);
      await newRoom.localParticipant.setMicrophoneEnabled(true);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to start call');
      setIsConnecting(false);
    }
  };

  const stopSession = async () => {
    try {
      if (room) { await room.disconnect(); setRoom(null); }
      if (sessionId) await api.post(`/sessions/${sessionId}/end`);
      videoAttachedRef.current = false;
      avatarReadyNotifiedRef.current = false;
      setIsConnected(false);
      setSessionId(null);
      setCaptions([]);
      setAvatarVideoTrack(null);
      setActiveProductId(null);
      allCaptionsRef.current = [];
      productDisplayQueueRef.current = [];
      productDisplayIndexRef.current = 0;
      lastProductSwitchRef.current = 0;
    } catch {}
  };

  // Fallback poll for avatar video (events are primary, this catches edge cases)
  useEffect(() => {
    if (!room || !isConnected) return;
    const check = () => {
      if (videoAttachedRef.current) return;
      room.remoteParticipants.forEach((p) => {
        const isAvatar = p.identity === 'liveavatar-avatar-agent' || p.name === 'liveavatar-avatar-agent' ||
          (p.identity && p.identity.includes('liveavatar')) || (p.name && p.name.includes('liveavatar'));
        if (isAvatar && !videoAttachedRef.current) {
          p.videoTrackPublications.forEach((pub) => {
            if (pub.track && !videoAttachedRef.current) {
              try { pub.setVideoQuality(VideoQuality.LOW); pub.setVideoFPS(30); } catch {}
              setAvatarVideoTrack(pub.track);
              if (videoRef.current) {
                try {
                  pub.track.attach(videoRef.current);
                  if (!videoRef.current.srcObject && pub.track.mediaStreamTrack) {
                    videoRef.current.srcObject = new MediaStream([pub.track.mediaStreamTrack]);
                  }
                  videoAttachedRef.current = true;
                  videoRef.current.play().catch(() => {});
                } catch {}
              }
            }
          });
        }
      });
    };
    check();
    const interval = setInterval(check, 500);
    return () => clearInterval(interval);
  }, [room, isConnected]);

  useEffect(() => {
    if (avatarVideoTrack && videoRef.current) {
      const t = setTimeout(() => { videoRef.current?.play().catch(() => {}); }, 200);
      return () => clearTimeout(t);
    }
  }, [avatarVideoTrack]);

  useEffect(() => {
    return () => {
      if (fpsIntervalRef.current) { clearInterval(fpsIntervalRef.current); fpsIntervalRef.current = null; }
      if (room) room.disconnect();
    };
  }, [room]);

  const activeProduct = productImages.find((p) => p.id === activeProductId);
  const featuredProduct = productImages.find((p) => p.is_featured);
  const displayProduct = activeProduct || featuredProduct;
  const isImageOnly = displayProduct?.hide_name && displayProduct?.hide_description;

  if (loading) {
    return (
      <div className="min-h-screen bg-transparent flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-500 mx-auto mb-4" />
          <p className="text-gray-400">Loading agent...</p>
        </div>
      </div>
    );
  }

  if (error && !agent) {
    return (
      <div className="min-h-screen bg-transparent flex items-center justify-center">
        <div className="text-center px-4">
          <p className="text-red-400 text-lg mb-2">{error}</p>
          <p className="text-gray-500">Please check the agent configuration.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-transparent flex items-center justify-center p-0">
      <div className="w-full max-w-7xl">
        <div className="bg-transparent border border-gray-700/50 rounded-2xl overflow-hidden">
          {/* Main Content: Split View */}
          <div className="flex flex-col lg:flex-row">
            {/* LEFT: Avatar + Controls */}
            <div className="lg:w-1/2 flex flex-col border-r border-gray-700/50">
              {/* Dynamic Variable Input Fields */}
              {viewMode === 'dynamic' && dynamicVariableNames.length > 0 && (
                <div className="p-4 bg-gray-900/50 border-b border-gray-700/50">
                  <h3 className="text-xs font-semibold text-gray-400 mb-2">Your Information</h3>
                  <div className="space-y-2">
                    {dynamicVariableNames.map((varName) => (
                      <div key={varName} className="space-y-1">
                        <label className="text-xs font-medium text-gray-500 block">{getVariableLabel(varName)}</label>
                        <input
                          type="text"
                          value={dynamicVariableValues[varName] || ''}
                          onChange={(e) => updateVariableValue(varName, e.target.value)}
                          placeholder={`Enter ${getVariableLabel(varName).toLowerCase()}`}
                          disabled={isConnected}
                          className="w-full px-3 py-2 bg-gray-800/80 border border-gray-700 rounded-lg text-sm text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:opacity-60 disabled:cursor-not-allowed"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Video - fixed dimensions */}
              <div className="relative bg-transparent aspect-video lg:aspect-auto lg:h-[420px] w-full flex items-center justify-center">
                {avatarVideoTrack ? (
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted={false}
                    className="w-full h-full object-contain"
                    onLoadedMetadata={() => videoRef.current?.play().catch(() => {})}
                    onCanPlay={() => {
                      videoRef.current?.play().catch(() => {});
                      if (!avatarReadyNotifiedRef.current && room?.localParticipant) {
                        avatarReadyNotifiedRef.current = true;
                        try {
                          const msg = new TextEncoder().encode(JSON.stringify({ type: 'avatar_video_ready' }));
                          room.localParticipant.publishData(msg, { reliable: true, topic: 'avatar_status' });
                        } catch {}
                      }
                    }}
                  />
                ) : (
                  <div className="text-center">
                    {isConnected && (
                      <>
                        <Loader2 className="w-10 h-10 animate-spin text-indigo-500 mx-auto mb-3" />
                        <p className="text-gray-400 text-sm">Waiting for avatar...</p>
                      </>
                    )}
                  </div>
                )}

                {/* Call icon - centered when not connected, only the button is clickable */}
                {!isConnected && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <button
                      onClick={startSession}
                      disabled={isConnecting || !agent?.liveavatar_enabled}
                      className="pointer-events-auto w-16 h-16 rounded-full bg-indigo-600/90 hover:bg-indigo-500 flex items-center justify-center transition-all duration-200 hover:scale-110 disabled:hover:scale-100 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                    >
                      {isConnecting ? (
                        <Loader2 className="w-8 h-8 text-white animate-spin" />
                      ) : (
                        <Phone className="w-8 h-8 text-white" />
                      )}
                    </button>
                  </div>
                )}

                {/* End call icon - top left when connected */}
                {isConnected && (
                  <button
                    onClick={stopSession}
                    className="absolute top-3 left-3 w-10 h-10 rounded-full bg-red-600 hover:bg-red-500 flex items-center justify-center transition-all duration-200 hover:scale-110 shadow-lg z-10"
                    title="End call"
                  >
                    <PhoneOff className="w-5 h-5 text-white" />
                  </button>
                )}
              </div>

              {error && (
                <div className="px-4 pb-4">
                  <div className="p-2.5 bg-red-900/20 border border-red-800/50 rounded-lg">
                    <p className="text-red-400 text-xs">{error}</p>
                  </div>
                </div>
              )}
            </div>

            {/* RIGHT: Product Images - exact same dimensions as video */}
            <div className="lg:w-1/2 flex flex-col bg-transparent">
              {productImages.length === 0 ? (
                <div className="aspect-video lg:aspect-auto lg:h-[420px] flex items-center justify-center">
                  <div className="text-center">
                    <ShoppingBag className="w-12 h-12 mx-auto mb-3 text-gray-700" />
                    <p className="text-gray-500 text-sm">No products configured</p>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col min-h-0">
                  {/* Product display - same size as video when image-only */}
                  <div className={isImageOnly ? 'relative aspect-video lg:aspect-auto lg:h-[420px] flex items-center justify-center bg-transparent overflow-hidden' : 'flex flex-col'}>
                    {activeProduct ? (
                      activeProduct.hide_name && activeProduct.hide_description ? (
                        <div key={activeProductId} className="absolute inset-0 flex items-center justify-center">
                          <img
                            src={activeProduct.image_url}
                            alt={activeProduct.title}
                            className="w-full h-full object-contain animate-imageIn"
                          />
                        </div>
                      ) : (
                        <div key={activeProductId} className="flex flex-col animate-productChange">
                          <>
                            <div className="relative aspect-video lg:aspect-auto lg:h-[420px] bg-transparent overflow-hidden flex items-center justify-center">
                              <img
                                src={activeProduct.image_url}
                                alt={activeProduct.title}
                                className="w-full h-full object-contain p-2 animate-imageIn"
                              />
                            </div>
                            <div className="p-4">
                              {!activeProduct.hide_name && <h3 className="text-lg font-semibold text-white mb-1">{activeProduct.title}</h3>}
                              {!activeProduct.hide_description && <p className="text-gray-400 text-sm leading-relaxed line-clamp-3">{activeProduct.description}</p>}
                            </div>
                          </>
                        </div>
                      )
                    ) : featuredProduct ? (
                      featuredProduct.hide_name && featuredProduct.hide_description ? (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <img
                            src={featuredProduct.image_url}
                            alt={featuredProduct.title}
                            className="w-full h-full object-contain"
                          />
                        </div>
                      ) : (
                        <div className="flex flex-col">
                          <>
                            <div className="relative aspect-video lg:aspect-auto lg:h-[420px] bg-transparent overflow-hidden flex items-center justify-center">
                              <img
                                src={featuredProduct.image_url}
                                alt={featuredProduct.title}
                                className="w-full h-full object-contain p-2"
                              />
                            </div>
                            <div className="p-4">
                              {!featuredProduct.hide_name && <h3 className="text-lg font-semibold text-white mb-1">{featuredProduct.title}</h3>}
                              {!featuredProduct.hide_description && <p className="text-gray-400 text-sm leading-relaxed line-clamp-3">{featuredProduct.description}</p>}
                            </div>
                          </>
                        </div>
                      )
                    ) : (
                      <div className="aspect-video lg:aspect-auto lg:h-[420px] flex items-center justify-center">
                        <div className="text-center">
                          <ShoppingBag className="w-10 h-10 mx-auto mb-3 text-gray-700" />
                          <p className="text-gray-500 text-sm">No featured image configured</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <audio ref={audioRef} autoPlay />

      <style>{`
        @keyframes productChange {
          from { opacity: 0; transform: translateY(12px) scale(0.97); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes imageIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-productChange { animation: productChange 0.4s cubic-bezier(0.34, 1.56, 0.64, 1); }
        .animate-imageIn { animation: imageIn 0.35s ease-out; }
        .scrollbar-thin::-webkit-scrollbar { height: 4px; }
        .scrollbar-thin::-webkit-scrollbar-track { background: transparent; }
        .scrollbar-thin::-webkit-scrollbar-thumb { background: #374151; border-radius: 2px; }
        .line-clamp-3 { display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
      `}</style>
    </div>
  );
};

export default EmbedLiveAvatarProductsPage;
